// A/B messaging for the homepage narrative.
// A = original (punchy, abstract). B = clearer, plain-language "what we actually do".
// Titles use segment arrays: [{ t: "text" }, { t: "accent", a: true }]

export const COPY = {
  A: {
    label: "Original",
    hero_eyebrow: "Technology-enabled supply chain execution",
    hero_lines: [
      [{ t: "We don't plan" }],
      [{ t: "your supply chain." }],
      [{ t: "We " }, { t: "run", a: true }, { t: " it." }],
    ],
    hero_paragraph:
      "Eazymuv is one accountable operating partner across factory, warehouse and logistics — instrumented by the Eazymuv OS ecosystem. We close the execution gap where plans meet reality.",
    hero_cta_primary: "Discuss Your Operations",
    hero_cta_secondary: "Explore Eazymuv OS",
    hero_tiles: [
      ["Execution", "not dashboards"],
      ["One partner", "one accountability"],
      ["Land & expand", "site by site"],
      ["Eazymuv OS", "one operating view"],
    ],
    marquee: ["Execution", "EazyOS", "EazyMS", "Warehouse", "100% Visibility", "Land & Expand"],

    gap_eyebrow: "The Execution Gap",
    gap_title: [{ t: "Plans rarely fail. " }, { t: "Execution does.", a: true }],
    gap_para1:
      "Most supply chain value is lost between the plan and the floor — across fragmented vendors, siloed tools and hand-offs no one owns end to end. That gap is where cost, delay and risk accumulate.",
    gap_para2:
      "Eazymuv exists to close it: a single accountable operating partner that runs the work and instruments it with one technology ecosystem.",
    gap_cards: [
      { t: "Fragmented ownership", d: "Multiple vendors, no single accountable line." },
      { t: "Siloed data", d: "Plans and floor reality drift apart." },
      { t: "Fragile hand-offs", d: "Every transfer adds cost, delay and risk." },
    ],

    model_eyebrow: "The Operating Model",
    model_title: [{ t: "One accountable partner. One operating rhythm." }],
    model_para:
      "We take ownership of execution across the plant, the warehouse and logistics — with a single line of accountability and one connected operating view.",
    model_rows: [
      { t: "Managed warehouse & factory operations", d: "Inventory, labour and dispatch, run as one system." },
      { t: "Instrumented by Eazymuv OS", d: "The floor and the data tell the same story." },
      { t: "Managed logistics & dispatch", d: "Reliable movement of goods from your dock to your customers." },
    ],

    os_eyebrow: "The Technology Ecosystem",
    os_title: [{ t: "Eazymuv " }, { t: "OS", a: true }, { t: " — 100% visibility across your operations" }],
    os_para:
      "One connected operating system linking managed operations and EazyMS — so every facility, SKU and shipment lives in a single view.",

    product_title: [{ t: "The product behind the service" }],
    product_eazyms_tag: "Warehouse & Factory",
    product_eazyms_desc: "Manage inventory, labour and dispatch as one operating system on the floor.",

    outcomes_title: [{ t: "What one accountable model delivers" }],
    outcomes_items: [
      { k: "One", label: "accountable operating partner", note: "Single point of ownership across plan, warehouse and logistics." },
      { k: "OS", label: "one technology ecosystem", note: "EazyOS gives you 100% visibility across managed operations and EazyMS." },
      { k: "Land", label: "and expand model", note: "Start inside one facility, expand across the network." },
    ],

    different_title: [{ t: "Vendors coordinate. " }, { t: "We execute.", a: true }],

    factory_eyebrow: "The Engagement",
    factory_title: "We start inside the factory — not in a slide deck",

    land_title: [{ t: "Prove it in one site. Then scale the network." }],
    land_para:
      "We de-risk transformation by starting where the impact is clearest — one facility, one accountable rollout. Once execution is stable and instrumented, we expand across sites and logistics.",

    proof_title: [{ t: "Evidence over claims" }],
    proof_para:
      "We publish customer references, case studies and metrics only when they are verified and approved.",

    industries_title: [{ t: "Built for high-velocity, temperature-critical supply chains" }],

    final_eyebrow: "Let's run it",
    final_title: "Ready to close your execution gap?",
    final_para:
      "Start inside one facility. Prove the model. Expand across your network — with one accountable partner.",
    final_cta_primary: "Discuss Your Operations",
    final_cta_secondary: "Request an EazyMS Demo",
  },

  B: {
    label: "Clear (new)",
    hero_eyebrow: "Warehousing · Factory logistics · Freight — run for you",
    hero_lines: [
      [{ t: "Warehouses," }],
      [{ t: "factories, freight —" }],
      [{ t: "we " }, { t: "run", a: true }, { t: " them for you." }],
    ],
    hero_paragraph:
      "Eazymuv is the team that physically runs your warehousing, on-site factory logistics and freight — using our own software, Eazymuv OS, to keep every product moving. One team, one system, one point of contact.",
    hero_cta_primary: "Talk to our team",
    hero_cta_secondary: "See how it works",
    hero_tiles: [
      ["We run it", "hands-on operations"],
      ["One team", "warehousing + freight"],
      ["Our software", "tracks every move"],
      ["Start small", "scale when it works"],
    ],
    marquee: ["Warehousing", "Factory logistics", "Freight", "Live tracking", "One partner", "Cold chain"],

    gap_eyebrow: "The problem",
    gap_title: [{ t: "Your products get stuck " }, { t: "between too many hands.", a: true }],
    gap_para1:
      "Between your factory and your customer, goods pass through separate warehouse vendors, transporters and disconnected software. Things get delayed, lost and hard to trace — and no one owns the outcome.",
    gap_para2:
      "Eazymuv replaces that patchwork. We run the whole middle ourselves and give you one place to see and control it all.",
    gap_cards: [
      { t: "Too many vendors", d: "A different partner for each step, and no one accountable for the result." },
      { t: "No single view", d: "Your spreadsheet and the warehouse floor never quite match." },
      { t: "Costly hand-offs", d: "Every transfer between vendors adds delay, cost and risk." },
    ],

    model_eyebrow: "What we actually do",
    model_title: [{ t: "One team runs it all — and shows you everything." }],
    model_para:
      "We put our own people and software inside your operation to run warehousing, factory logistics and freight as a single service you can rely on.",
    model_rows: [
      { t: "We run your warehouses & factory stores", d: "Receiving, storage, picking and dispatch — staffed and managed by our team." },
      { t: "We track it live in Eazymuv OS", d: "Real-time stock, labour and shipment data in one dashboard you can trust." },
      { t: "We coordinate your logistics & dispatch", d: "Reliable movement of goods from your dock to your customers." },
    ],

    os_eyebrow: "Our software",
    os_title: [{ t: "Eazymuv " }, { t: "OS", a: true }, { t: " — your whole operation on one screen" }],
    os_para:
      "The software that runs behind everything we do. It connects your warehouses, stock, staff and shipments into one live view — so you always know exactly where things stand.",

    product_title: [{ t: "The product behind the service" }],
    product_eazyms_tag: "Warehouse & Factory App",
    product_eazyms_desc: "The app our teams use to run your floor — stock, labour and dispatch, all in one place.",

    outcomes_title: [{ t: "Why companies hand this to us" }],
    outcomes_items: [
      { k: "1", label: "one team to call", note: "Warehousing and freight under a single, accountable partner — not five vendors." },
      { k: "2", label: "one live dashboard", note: "See stock, labour and shipments in real time in Eazymuv OS." },
      { k: "3", label: "start small, scale later", note: "Prove it in one site, then roll out across your network." },
    ],

    different_title: [{ t: "3PLs and software coordinate. " }, { t: "We run it.", a: true }],

    factory_eyebrow: "How we start",
    factory_title: "We start inside one of your sites — not in a slide deck",

    land_title: [{ t: "Prove it in one site. Then scale." }],
    land_para:
      "We begin in a single warehouse or plant, get it running smoothly, then expand across your network and into freight — so you're never betting the whole operation at once.",

    proof_title: [{ t: "We only show results we can prove" }],
    proof_para:
      "We publish customer names and numbers only once they're verified and approved.",

    industries_title: [{ t: "Built for fast-moving and cold-chain products" }],

    final_eyebrow: "Let's talk",
    final_title: "Want us to run your supply chain?",
    final_para:
      "Tell us where things are breaking down. We'll show you how one team and one system can take warehousing and freight off your plate.",
    final_cta_primary: "Talk to our team",
    final_cta_secondary: "Request an EazyMS demo",
  },
};
